import { Component, OnInit, Input } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { UtilityService } from '@nttp/common-service/utility-service';
import { NewRecipientModel, NewRecipientStep1Model } from '@pg-app/client/manage-recipient/models/recipient.model';
import { SendPaymentInstructionModelService } from '@pg-app/client/send-payment-instruction/services/send-payment-instruction.model.service';
import { ShareService } from '@nttp/common-service/share-service';
import { MatDialog } from '@angular/material/dialog';
import { SendPaymentInstructionComponent } from '@pg-app/client/send-payment-instruction/containers/send-payment/send-payment-instruction.component';
import { countryCodes, currencyCodes } from '@pg-app/client/manage-recipient/constants/add-recipient-constants';
import { BeneficiaryTypes } from '@pg-app/client/payment/constants/beneficiary-type-list';
import { CommonMessages } from '@pg-app/shared/constants/common-messages';
import { CommonDialogComponent } from '@pg-app/common/common-dialog/common-dialog.component';
import { SendPaymentTypeEnum } from '@nttp/enum/send-payment-type.enum';
import { AddRecipientFrom } from '@pg-app/client/manage-recipient/constants/add-recipient-constants';
import { RecipientService } from '@pg-app/client/manage-recipient/services/recipient.service';
import { recipientType } from '@pg-app/client/manage-recipient/constants/recipient-type-constants';
import { Regex } from '@pg-app/shared/constants/regex';
import { numberOfWordBeneficiaryNameValidator } from '@pg-app/client/manage-recipient/constants/beneficiary-name-number-of-word-validator';
import { AddBeneficiaryStep } from '@pg-app/client/manage-recipient/constants/add-beneficiary-constant';
import { BankingTypes } from '@pg-app/shared/constants/banking-types-constants';

@Component({
  selector: 'np-add-new-recipient-step-1',
  templateUrl: './step-1.component.html',
  styleUrls: ['./step-1.component.scss']
})
export class AddNewRecipientStep1Component implements OnInit {
  @Input()
  action = AddRecipientFrom.ManageRecipient;
  addRecipientFrom = AddRecipientFrom;

  formStep1: FormGroup;
  countries: Array<any> = [];
  currencies: Array<any> = [];
  showEmail = true;
  beneTypes = BeneficiaryTypes;

  recipientTypeOptions = recipientType;

  requireAddress = false;
  requireTaxId = false;

  constructor(
    private fb: FormBuilder,
    private router: Router,
    public newRecipientModel: NewRecipientModel,
    private utilityService: UtilityService,
    private sendPaymentInstructionModel: SendPaymentInstructionModelService,
    private recipientService: RecipientService,
    private shareService: ShareService,
    private dialog: MatDialog,
  ) { }

  ngOnInit() {
    if (this.action !== AddRecipientFrom.Bank) {
      this.formStep1 = this.fb.group({
        recipientCountry: [this.newRecipientModel.step1.recipientCountry.name],
        recipientAddress1: [this.newRecipientModel.step1.recipientAddress1 || null, [Validators.maxLength(35), Validators.pattern(Regex.nonSwiftCharacters)]],
        recipientAddress2: [this.newRecipientModel.step1.recipientAddress2 || null, [Validators.maxLength(35), Validators.pattern(Regex.nonSwiftCharacters)]],
        currency: [this.newRecipientModel.step1.currency.code || null, Validators.required],
        recipientName: [this.newRecipientModel.step1.recipientName || null, [Validators.required, Validators.pattern(Regex.swiftAndSquareBracket)]],
        notification: [this.newRecipientModel.step1.notification || 'no', Validators.required],
        country: [this.newRecipientModel.step1.country.name, Validators.required],
        beneType: [this.newRecipientModel.step1.beneficiaryType, Validators.required],
        beneCity: [this.newRecipientModel.step1.beneficiaryCity || null, [Validators.maxLength(60), Validators.pattern(Regex.nonSwiftCharacters)]],
        beneState: [this.newRecipientModel.step1.beneficiaryState || null, [Validators.maxLength(50), Validators.pattern(Regex.nonSwiftCharacters)]],
        benePostCode: [this.newRecipientModel.step1.beneficiaryPostCode || null, [Validators.maxLength(12), Validators.pattern(Regex.nonSwiftCharacters)]],
        beneTaxId: [this.newRecipientModel.step1.beneficiaryTaxId || null, [Validators.pattern(Regex.nonSwiftCharacters)]]
      },
        {
          validator: numberOfWordBeneficiaryNameValidator()
        });
    } else {
      this.formStep1 = this.fb.group({
        country: ['', Validators.required],
        currency: ['', Validators.required],
      });
    }

    this.getCurrencies();
    this.getCountries();
    this.getBankingConfig();

    if (this.action !== AddRecipientFrom.Bank && this.formStep1.get('notification').value === 'no') {
      this.showEmail = false;
      this.formStep1.removeControl('email');
    }

    this.checkRequiredBeneficiaryDetails();
    this.checkRequiredStateProvince();
  }

  submitFromStep1() {
    this.newRecipientModel.clear();
    this.formStep1.markAllAsTouched();

    if (!this.formStep1.valid) {
      return;
    }

    const selectedBankCountry = this.countries.find(item => item.name.toLowerCase() === this.formStep1.get('country').value.toLowerCase());
    const selectedCurrency = this.currencies.find(item => item.code.toLowerCase() === this.formStep1.get('currency').value.toLowerCase());

    if (!selectedBankCountry || !selectedCurrency) {
      return;
    }

    this.newRecipientModel.step1.currency = {
      id: selectedCurrency.id,
      code: selectedCurrency.code
    };

    this.newRecipientModel.step1.country = {
      id: selectedBankCountry.id,
      name: selectedBankCountry.name,
      code: selectedBankCountry.code,
      isEurozone: selectedBankCountry.isEurozone,
      hasUseIBAN: selectedBankCountry.hasUseIBAN,
      isFasterPay: selectedBankCountry.isFasterPay
    };

    this.newRecipientModel.step1.countries = this.countries;

    if (this.action !== AddRecipientFrom.Bank) {
      const selectedBeneCountry = this.countries.find(item => item.name.toLowerCase() === this.formStep1.get('recipientCountry').value.toLowerCase());
      if ((this.formStep1.get('recipientCountry').value && !selectedBeneCountry)) {
        return;
      }
      if (selectedBeneCountry) {
        this.newRecipientModel.step1.recipientCountry = {
          id: selectedBeneCountry.id,
          name: selectedBeneCountry.name
        };
      }
      this.newRecipientModel.step1.recipientAddress1 = this.formStep1.get('recipientAddress1').value;
      this.newRecipientModel.step1.recipientAddress2 = this.formStep1.get('recipientAddress2').value;

      this.newRecipientModel.step1.recipientName = this.formStep1.get('recipientName').value;
      this.newRecipientModel.step1.notification = this.formStep1.get('notification').value;
      this.newRecipientModel.step1.email = this.showEmail ? this.formStep1.get('email').value : '';

      this.newRecipientModel.step1.beneficiaryType = this.formStep1.get('beneType').value;
      this.newRecipientModel.step1.beneficiaryCity = this.formStep1.get('beneCity').value;
      this.newRecipientModel.step1.beneficiaryState = this.formStep1.get('beneState').value;
      this.newRecipientModel.step1.beneficiaryPostCode = this.formStep1.get('benePostCode').value;
      this.newRecipientModel.step1.beneficiaryTaxId = this.formStep1.get('beneTaxId').value;

      // Check if the country code/currency code combination accepts SWIFT
      const countryCode = this.newRecipientModel.step1.country.code;
      const currencyCode = this.newRecipientModel.step1.currency.code;
      const queryString = 'countryCode=' + countryCode + '&currencyCode=' + currencyCode;
      this.recipientService.acceptSWIFT(queryString).subscribe(
        response => {
          this.newRecipientModel.step1.acceptSWIFT = response;

          // Go to STEP 2
          this.newRecipientModel.currentStep = AddBeneficiaryStep.STEP2;
        },
      );
    } else {
      this.showDialog();
    }
  }

  cancel() {
    this.newRecipientModel.step1 = new NewRecipientStep1Model();
    if (this.sendPaymentInstructionModel.manageRecipientFromPayment) {
      this.sendPaymentInstructionModel.step = this.sendPaymentInstructionModel.sendPaymentStep.paymentInfo;
      this.router.navigate(['/client/dashboard']);
      if (this.sendPaymentInstructionModel.sendPaymentType === SendPaymentTypeEnum.SendPaymentFromTrade) {
        this.dialog.open(SendPaymentInstructionComponent, {
          disableClose: true,
          panelClass: 'custom-dialog',
        });
      }
      return;
    }

    const url = this.action === AddRecipientFrom.Bank ? 'partner/user-profile' : 'client/manage-recipient';
    this.newRecipientModel.clear();
    this.router.navigate([`${url}`]);
  }

  private getCountries() {
    this.shareService.countryList$.subscribe(
      data => {
        data.forEach(item => {
          const flagIcon = (item.code.length <= 2 ? item.code : item.code.substring(0, 2)).toLowerCase();
          item.flag = `flag-icon flag-icon-${flagIcon}`;
        });
        this.countries = data || [];
        if (this.action === AddRecipientFrom.Bank) {
          const country = this.countries.find(p => p.id === this.newRecipientModel.step1.country.id);
          this.formStep1.get('country').setValue(country.name);
        }
      },
    );
  }

  private getCurrencies() {
    this.utilityService.getCurrencies().subscribe(
      data => {
        data.forEach(item => {
          const flagIcon = (item.code.length <= 2 ? item.code : item.code.substring(0, 2)).toLowerCase();
          item.flag = `flag-icon flag-icon-${flagIcon}`;
        });
        this.currencies = data || [];
        if (this.action === AddRecipientFrom.Bank || this.action === AddRecipientFrom.SendPayment) {
          const currencyId = this.action === AddRecipientFrom.Bank ? this.newRecipientModel.step1.currency.id : this.sendPaymentInstructionModel.currency.id;
          const currency = this.currencies.find(p => p.id === currencyId);
          this.formStep1.get('currency').setValue(currency.code);
        }
      },
    );
  }

  focusNo() {
    this.showEmail = false;
    this.formStep1.removeControl('email');
  }

  focusYes() {
    this.showEmail = true;
    const defaultEmailValidator = [
      Validators.required,
      Validators.maxLength(255),
      Validators.email
    ];
    this.formStep1.addControl('email', new FormControl(null, defaultEmailValidator));
    setTimeout(() => {
      window.scrollTo({
        top: document.body.scrollHeight,
        behavior: 'smooth'
      });
    }, 10);
  }

  showDialog() {
    if (this.newRecipientModel.step1.currency.code !== currencyCodes.gbp) {
      const dialogRef = this.dialog.open(CommonDialogComponent, {
        disableClose: true,
        data: {
          title: CommonMessages.currencyNotGBP,
          dialogType: 3,
          routingUrl: null
        }
      });
      dialogRef.afterClosed().subscribe(res => {
        if (res) {
          this.newRecipientModel.currentStep = AddBeneficiaryStep.STEP2;
        }
      });
    } else {
      this.newRecipientModel.currentStep = AddBeneficiaryStep.STEP2;
    }
  }

  checkRequiredBeneficiaryDetails() {
    if (this.action === AddRecipientFrom.Bank) {
      return;
    }

    const countryName = this.formStep1.get('country').value;
    const country = countryName ? this.countries.find(p => p.name.toLowerCase() === countryName.toLowerCase()) : null;
    const currencyCode = this.formStep1.get('currency').value ? this.formStep1.get('currency').value.toUpperCase() : null;
    const currency = currencyCode ? this.currencies.find(p => p.code.toLowerCase() === currencyCode.toLowerCase()) : null;

    if (!currency || !country) {
      return;
    }

    this.getBankingType(country, currency);
  }

  checkRequiredStateProvince() {
    if (this.action === AddRecipientFrom.Bank) {
      return;
    }
    const countryName = this.formStep1.get('country').value;
    const country = this.countries.find(p => p.name.toLowerCase() === countryName.toLowerCase());

    // require beneficiary state or province get from BE config
    if (country && this.newRecipientModel.bankConfig.requireStateProvince.includes(country.code)) {
      this.formStep1.get('beneState').setValidators([Validators.required, Validators.maxLength(50), Validators.pattern(Regex.nonSwiftCharacters)]);
    } else {
      this.formStep1.get('beneState').setValidators([Validators.maxLength(50), Validators.pattern(Regex.nonSwiftCharacters)]);
    }
    this.formStep1.get('beneState').updateValueAndValidity();
  }

  getBankingConfig() {
    this.recipientService.getBankingConfig().subscribe(res => {
      this.newRecipientModel.bankConfig = res;
    });
  }

  getBankingType(country, currency) {
    let dto = {
      countryId: country.id,
      currencyId: currency.id
    };
    this.shareService.getBankingType(dto).subscribe(bankingType => {

      // GPB to Faster Pay countries or EUR to Euro zone country
      // Address1, city, post code, recipient country are optional
      if (bankingType === BankingTypes.Case1_FasterPay_GBP ||
        (currency.code === currencyCodes.eur && country.isEurozone)) {
        this.formStep1.get('recipientAddress1').setValidators([Validators.maxLength(35), Validators.pattern(Regex.nonSwiftCharacters)]);
        this.formStep1.get('beneCity').setValidators([Validators.maxLength(60), Validators.pattern(Regex.nonSwiftCharacters)]);
        this.formStep1.get('benePostCode').setValidators([Validators.maxLength(12), Validators.pattern(Regex.nonSwiftCharacters)]);
        this.formStep1.get('recipientCountry').clearValidators();
      } else {
        this.formStep1.get('recipientAddress1').setValidators([Validators.required, Validators.maxLength(35), Validators.pattern(Regex.nonSwiftCharacters)]);
        this.formStep1.get('beneCity').setValidators([Validators.required, Validators.maxLength(60), Validators.pattern(Regex.nonSwiftCharacters)]);
        this.formStep1.get('benePostCode').setValidators([Validators.required, Validators.maxLength(12), Validators.pattern(Regex.nonSwiftCharacters)]);
        this.formStep1.get('recipientCountry').setValidators(Validators.required);
      }

      // require beneficiary Tax Id get from BE config
      const beneTaxIdMandatory = !!this.newRecipientModel.bankConfig.requireTaxId.find(p => p.countryCode === country.code && p.currencyCode === currency.code);
      if (beneTaxIdMandatory) {
        this.formStep1.get('beneTaxId').setValidators([Validators.required, Validators.pattern(Regex.nonSwiftCharacters)]);
      } else {
        this.formStep1.get('beneTaxId').setValidators([Validators.pattern(Regex.nonSwiftCharacters)]);
      }

      this.formStep1.get('recipientAddress1').updateValueAndValidity();
      this.formStep1.get('beneCity').updateValueAndValidity();
      this.formStep1.get('benePostCode').updateValueAndValidity();
      this.formStep1.get('recipientCountry').updateValueAndValidity();
      this.formStep1.get('beneTaxId').updateValueAndValidity();
    });
  }

}
